/* Review Carousel
   ---------------------------------------------------------
   Equal-width cards
   Native horizontal scrolling
   Previous / Next navigation
   Dot navigation
   Mouse drag
   Touch drag
   Momentum scrolling
   Product links remain clickable
   Shopify Theme Editor support
   --------------------------------------------------------- */

(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';

  var DESKTOP_VISIBLE = 4;
  var MOBILE_VISIBLE = 1.5;

  var RESIZE_DEBOUNCE = 150;

  var DRAG_THRESHOLD = 5;

  var MOMENTUM_MULTIPLIER = 1.35;
  var MOMENTUM_FRICTION = 0.94;
  var MOMENTUM_MIN_VELOCITY = 0.2;

  var CLICK_SUPPRESS_TIME = 100;


  /* =========================================================
     HELPERS
     ========================================================= */

  function debounce(fn, wait) {
    var timer;

    return function () {
      var args = arguments;
      var context = this;

      clearTimeout(timer);

      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }


  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }


  /* =========================================================
     CAROUSEL
     ========================================================= */

  function ReviewCarousel(root) {
    this.root = root;

    this.viewport =
      root.querySelector('[data-rc-viewport]');

    this.track =
      root.querySelector('[data-rc-track]');

    this.prevBtn =
      root.querySelector('[data-rc-prev]');

    this.nextBtn =
      root.querySelector('[data-rc-next]');

    this.dotsWrap =
      root.querySelector('[data-rc-dots]');


    if (!this.viewport || !this.track) {
      return;
    }


    this.cards = Array.prototype.slice.call(
      this.track.children
    );


    if (!this.cards.length) {
      return;
    }


    this.index = 0;

    this.dots = [];

    this.dotPositions = [];

    this.visibleCount =
      this.getVisibleCount();


    /* -------------------------------------------------------
       Drag state
       ------------------------------------------------------- */

    this.pointerDown = false;
    this.isDragging = false;

    this.pointerId = null;

    this.pointerStartX = 0;
    this.pointerLastX = 0;

    this.dragStartScrollLeft = 0;

    this.lastMoveTime = 0;
    this.velocity = 0;

    this.momentumFrame = null;

    this.suppressClick = false;


    /* -------------------------------------------------------
       Event handlers
       ------------------------------------------------------- */

    this.handleResize =
      debounce(
        this.onResize.bind(this),
        RESIZE_DEBOUNCE
      );

    this.handleScroll =
      this.onScroll.bind(this);


    this.bindEvents();

    this.configureNativeScrolling();

    /*
     * Wait one frame so CSS card dimensions are fully
     * calculated before measuring positions.
     */
    var self = this;

    window.requestAnimationFrame(function () {
      self.refresh();
    });
  }


  /* =========================================================
     VISIBLE COUNT
     ========================================================= */

  ReviewCarousel.prototype.getVisibleCount =
    function () {

      return window.matchMedia(DESKTOP_QUERY).matches
        ? DESKTOP_VISIBLE
        : MOBILE_VISIBLE;
    };


  /* =========================================================
     MAX SCROLL
     ========================================================= */

  ReviewCarousel.prototype.getMaxScroll =
    function () {

      return Math.max(
        0,
        this.viewport.scrollWidth -
          this.viewport.clientWidth
      );
    };


  /* =========================================================
     NATIVE SCROLLING
     ========================================================= */

  ReviewCarousel.prototype.configureNativeScrolling =
    function () {

      /*
       * Make sure no transform from an older version
       * can interfere with native scrolling.
       */
      this.track.style.transform = 'none';

      /*
       * Native horizontal scrolling.
       */
      this.viewport.style.overflowX = 'auto';
      this.viewport.style.overflowY = 'hidden';

      /*
       * The JavaScript handles positioning.
       * Do not let browser scroll snapping interfere.
       */
      this.viewport.style.scrollSnapType = 'none';
      this.track.style.scrollSnapType = 'none';

      /*
       * Hide scrollbar.
       */
      this.viewport.style.scrollbarWidth = 'none';
      this.viewport.style.msOverflowStyle = 'none';
    };


  /* =========================================================
     CARD POSITIONS
     ========================================================= */

  // ReviewCarousel.prototype.getCardPositions =
  //   function () {

  //     var maxScroll =
  //       this.getMaxScroll();

  //     return this.cards.map(function (card) {

  //       /*
  //        * offsetLeft gives the true horizontal position
  //        * of the card inside the flex track.
  //        */
  //       return clamp(
  //         card.offsetLeft,
  //         0,
  //         maxScroll
  //       );
  //     });
  //   };



  ReviewCarousel.prototype.getCardPositions =
  function () {

    var maxScroll =
      this.getMaxScroll();

    /*
     * Use the first card as the horizontal origin.
     * This guarantees the first card is always at scrollLeft = 0,
     * even if the track has left padding/margin/offset.
     */
    var firstCardOffset =
      this.cards.length
        ? this.cards[0].offsetLeft
        : 0;

    return this.cards.map(function (card) {

      return clamp(
        card.offsetLeft - firstCardOffset,
        0,
        maxScroll
      );

    });
  };


  /* =========================================================
     NAVIGATION POSITIONS
     ========================================================= */

  ReviewCarousel.prototype.getNavigationPositions =
    function () {

      var positions =
        this.getCardPositions();

      var maxScroll =
        this.getMaxScroll();

      var validPositions = [];

      for (
        var i = 0;
        i < positions.length;
        i++
      ) {

        var position =
          clamp(
            positions[i],
            0,
            maxScroll
          );


        /*
         * Do not create duplicate destinations.
         *
         * Example:
         *
         * card 1 = 0
         * card 2 = 300
         * card 3 = 600
         * card 4 = 900
         * card 5 = maxScroll
         *
         * Cards 6, 7, 8 may also resolve to maxScroll.
         */
        if (
          validPositions.length === 0 ||
          Math.abs(
            validPositions[
              validPositions.length - 1
            ] - position
          ) > 2
        ) {
          validPositions.push(position);
        }
      }


      /*
       * Always make sure the absolute final scroll position
       * is available.
       */
      if (
        validPositions.length === 0 ||
        Math.abs(
          validPositions[
            validPositions.length - 1
          ] - maxScroll
        ) > 2
      ) {
        validPositions.push(maxScroll);
      }


      return validPositions;
    };


  /* =========================================================
     FIND NEAREST CARD
     ========================================================= */

  ReviewCarousel.prototype.getNearestCardIndex =
    function () {

      var positions =
        this.getCardPositions();

      if (!positions.length) {
        return 0;
      }


      var scrollLeft =
        this.viewport.scrollLeft;

      var nearestIndex = 0;
      var nearestDistance = Infinity;


      for (
        var i = 0;
        i < positions.length;
        i++
      ) {

        var distance =
          Math.abs(
            positions[i] - scrollLeft
          );


        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = i;
        }
      }


      /*
       * When the carousel has reached its absolute end,
       * the last card should be considered active.
       */
      var maxScroll =
        this.getMaxScroll();


      if (
        maxScroll > 0 &&
        Math.abs(
          scrollLeft - maxScroll
        ) <= 2
      ) {
        nearestIndex =
          positions.length - 1;
      }


      return nearestIndex;
    };


  /* =========================================================
     FIND NAVIGATION INDEX
     ========================================================= */

  ReviewCarousel.prototype.getNearestNavigationIndex =
    function () {

      var positions =
        this.dotPositions;

      if (!positions || !positions.length) {
        return 0;
      }


      var scrollLeft =
        this.viewport.scrollLeft;

      var nearestIndex = 0;
      var nearestDistance = Infinity;


      for (
        var i = 0;
        i < positions.length;
        i++
      ) {

        var distance =
          Math.abs(
            positions[i] - scrollLeft
          );


        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = i;
        }
      }


      return nearestIndex;
    };


  /* =========================================================
     DOTS
     ========================================================= */

  ReviewCarousel.prototype.buildDots =
    function () {

      if (!this.dotsWrap) {
        return;
      }


      this.dotPositions =
        this.getNavigationPositions();


      this.dotsWrap.innerHTML = '';

      this.dots = [];


      for (
        var i = 0;
        i < this.dotPositions.length;
        i++
      ) {

        var dot =
          document.createElement('button');

        dot.type = 'button';

        dot.className =
          'review-carousel__dot';

        dot.setAttribute(
          'aria-label',
          'Go to review ' + (i + 1)
        );


        dot.addEventListener(
          'click',
          this.goTo.bind(this, i)
        );


        this.dotsWrap.appendChild(dot);

        this.dots.push(dot);
      }
    };


  /* =========================================================
     UPDATE INDEX
     ========================================================= */

  ReviewCarousel.prototype.updateIndexFromScroll =
    function () {

      /*
       * Update card index for arrow navigation.
       */
      var cardIndex =
        this.getNearestCardIndex();


      this.index = cardIndex;


      /*
       * Update active dot independently.
       */
      var dotIndex =
        this.getNearestNavigationIndex();


      for (
        var i = 0;
        i < this.dots.length;
        i++
      ) {

        this.dots[i].classList.toggle(
          'is-active',
          i === dotIndex
        );
      }


      this.updateControls();
    };


  /* =========================================================
     SCROLL EVENT
     ========================================================= */

  ReviewCarousel.prototype.onScroll =
    function () {

      if (this.scrollRAF) {
        return;
      }


      var self = this;


      this.scrollRAF =
        window.requestAnimationFrame(
          function () {

            self.scrollRAF = null;

            self.updateIndexFromScroll();
          }
        );
    };


  /* =========================================================
     CONTROLS
     ========================================================= */

  ReviewCarousel.prototype.updateControls =
    function () {

      var currentScroll =
        this.viewport.scrollLeft;

      var maxScroll =
        this.getMaxScroll();


      if (this.prevBtn) {
        this.prevBtn.disabled =
          currentScroll <= 1;
      }


      if (this.nextBtn) {
        this.nextBtn.disabled =
          currentScroll >=
          maxScroll - 1;
      }


      /*
       * If there is no overflow,
       * both buttons are disabled.
       */
      if (maxScroll <= 1) {

        if (this.prevBtn) {
          this.prevBtn.disabled = true;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = true;
        }
      }
    };


  /* =========================================================
     GO TO DOT
     ========================================================= */

  ReviewCarousel.prototype.goTo =
    function (dotIndex) {

      if (
        !this.dotPositions ||
        !this.dotPositions.length
      ) {
        return;
      }


      var targetIndex =
        clamp(
          dotIndex,
          0,
          this.dotPositions.length - 1
        );


      var target =
        this.dotPositions[targetIndex];


      this.stopMomentum();


      /*
       * Update immediately so rapid arrow/dot clicks
       * remain deterministic during smooth scrolling.
       */
      this.index =
        clamp(
          targetIndex,
          0,
          this.cards.length - 1
        );


      this.viewport.scrollTo({
        left: target,
        behavior: 'smooth'
      });


      this.updateControls();
    };


  /* =========================================================
     GO TO CARD
     ========================================================= */

  ReviewCarousel.prototype.goToCard =
    function (cardIndex) {

      var positions =
        this.getCardPositions();

      if (!positions.length) {
        return;
      }


      var targetIndex =
        clamp(
          cardIndex,
          0,
          positions.length - 1
        );


      var maxScroll =
        this.getMaxScroll();


      var target =
        clamp(
          positions[targetIndex],
          0,
          maxScroll
        );


      this.stopMomentum();


      this.index = targetIndex;


      this.viewport.scrollTo({
        left: target,
        behavior: 'smooth'
      });


      this.updateControls();
    };


  /* =========================================================
     NEXT
     ========================================================= */

  // ReviewCarousel.prototype.next =
  //   function () {

  //     var currentIndex =
  //       this.getNearestCardIndex();


  //     var targetIndex =
  //       currentIndex + 1;


  //     if (
  //       targetIndex >= this.cards.length
  //     ) {
  //       targetIndex =
  //         this.cards.length - 1;
  //     }


      /*
       * Do nothing when already at the end.
       */
    //   var maxScroll =
    //     this.getMaxScroll();

    //   if (
    //     this.viewport.scrollLeft >=
    //     maxScroll - 1
    //   ) {
    //     return;
    //   }


    //   this.goToCard(targetIndex);
    // };


ReviewCarousel.prototype.next =
  function () {

    var maxScroll =
      this.getMaxScroll();

    var currentScroll =
      this.viewport.scrollLeft;

    if (currentScroll >= maxScroll - 1) {
      return;
    }

    /*
     * Move exactly one card width + gap.
     * This avoids relying on the last card's offsetLeft,
     * which can be clamped to maxScroll.
     */
    var cardWidth =
      this.cards[0].getBoundingClientRect().width;

    var step =
      cardWidth + 16; // card gap

    var target =
      Math.min(
        currentScroll + step,
        maxScroll
      );

    this.stopMomentum();

    this.viewport.scrollTo({
      left: target,
      behavior: 'smooth'
    });
  };





  /* =========================================================
     PREVIOUS
     ========================================================= */

  // ReviewCarousel.prototype.prev =
  //   function () {

  //     var currentIndex =
  //       this.getNearestCardIndex();


  //     var targetIndex =
  //       currentIndex - 1;


  //     if (targetIndex < 0) {
  //       targetIndex = 0;
  //     }


      /*
       * Do nothing when already at the beginning.
       */
    //   if (
    //     this.viewport.scrollLeft <= 1
    //   ) {
    //     return;
    //   }


    //   this.goToCard(targetIndex);
    // };


ReviewCarousel.prototype.prev =
  function () {

    var currentScroll =
      this.viewport.scrollLeft;

    if (currentScroll <= 1) {
      return;
    }

    /*
     * Move exactly one card width + gap backwards.
     * This works correctly even when already at maxScroll.
     */
    var cardWidth =
      this.cards[0].getBoundingClientRect().width;

    var step =
      cardWidth + 16; // card gap

    var target =
      Math.max(
        currentScroll - step,
        0
      );

    this.stopMomentum();

    this.viewport.scrollTo({
      left: target,
      behavior: 'smooth'
    });
  };





  /* =========================================================
     DRAG START
     ========================================================= */

  ReviewCarousel.prototype.startDrag =
    function (event) {

      /*
       * Ignore right/middle mouse buttons.
       */
      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }


      /*
       * Product links should behave like normal links.
       *
       * This prevents dragging from beginning when the user
       * is attempting to click the Shopify product link.
       */
      var interactiveTarget =
        event.target.closest(
          '[data-rc-product-link]'
        );


      if (interactiveTarget) {
        return;
      }


      this.stopMomentum();


      this.pointerDown = true;
      this.isDragging = false;

      this.pointerId =
        event.pointerId;


      this.pointerStartX =
        event.clientX;

      this.pointerLastX =
        event.clientX;


      this.dragStartScrollLeft =
        this.viewport.scrollLeft;


      this.lastMoveTime =
        performance.now();

      this.velocity = 0;


      try {
        this.viewport.setPointerCapture(
          event.pointerId
        );
      } catch (error) {
        /* Pointer capture unavailable. */
      }
    };


  /* =========================================================
     DRAG MOVE
     ========================================================= */

  ReviewCarousel.prototype.drag =
    function (event) {

      if (
        !this.pointerDown ||
        event.pointerId !== this.pointerId
      ) {
        return;
      }


      var currentX =
        event.clientX;


      var delta =
        currentX -
        this.pointerStartX;


      /*
       * Ignore tiny movement.
       */
      if (
        !this.isDragging &&
        Math.abs(delta) >= DRAG_THRESHOLD
      ) {
        this.isDragging = true;
      }


      if (!this.isDragging) {
        return;
      }


      /*
       * Prevent text selection and browser drag behavior.
       */
      event.preventDefault();


      /*
       * Left drag -> scroll right.
       * Right drag -> scroll left.
       */
      var targetScroll =
        this.dragStartScrollLeft -
        delta;


      var maxScroll =
        this.getMaxScroll();


      /*
       * Slight resistance at both edges.
       */
      if (targetScroll < 0) {

        targetScroll =
          targetScroll * 0.35;

      } else if (
        targetScroll > maxScroll
      ) {

        targetScroll =
          maxScroll +
          (
            targetScroll -
            maxScroll
          ) * 0.35;
      }


      this.viewport.scrollLeft =
        targetScroll;


      /*
       * Calculate velocity for momentum.
       */
      var now =
        performance.now();


      var elapsed =
        Math.max(
          1,
          now -
          this.lastMoveTime
        );


      var movement =
        currentX -
        this.pointerLastX;


      this.velocity =
        -movement / elapsed;


      this.pointerLastX =
        currentX;

      this.lastMoveTime =
        now;
    };


  /* =========================================================
     DRAG END
     ========================================================= */

  ReviewCarousel.prototype.endDrag =
    function (event) {

      if (
        !this.pointerDown ||
        event.pointerId !== this.pointerId
      ) {
        return;
      }


      this.pointerDown = false;


      try {
        this.viewport.releasePointerCapture(
          event.pointerId
        );
      } catch (error) {
        /* Pointer capture unavailable. */
      }


      /*
       * Suppress the synthetic click produced after
       * an actual drag.
       */
      if (this.isDragging) {

        this.suppressClick = true;


        var self = this;


        window.setTimeout(
          function () {
            self.suppressClick = false;
          },
          CLICK_SUPPRESS_TIME
        );
      }


      /*
       * Momentum only after real dragging.
       */
      if (
        this.isDragging &&
        Math.abs(this.velocity) >
          MOMENTUM_MIN_VELOCITY
      ) {

        this.startMomentum();
      }


      this.isDragging = false;
      this.pointerId = null;
    };


  /* =========================================================
     MOMENTUM
     ========================================================= */

  ReviewCarousel.prototype.startMomentum =
    function () {

      this.stopMomentum();


      var self = this;


      var velocity =
        this.velocity *
        MOMENTUM_MULTIPLIER;


      function animate() {

        velocity *=
          MOMENTUM_FRICTION;


        if (
          Math.abs(velocity) <
          MOMENTUM_MIN_VELOCITY
        ) {

          self.momentumFrame = null;

          return;
        }


        var maxScroll =
          self.getMaxScroll();


        var next =
          self.viewport.scrollLeft +
          velocity * 16;


        /*
         * Clamp at the beginning.
         */
        if (next <= 0) {

          next = 0;

          velocity = 0;
        }


        /*
         * Clamp at the end.
         */
        if (next >= maxScroll) {

          next = maxScroll;

          velocity = 0;
        }


        self.viewport.scrollLeft =
          next;


        if (
          Math.abs(velocity) >=
          MOMENTUM_MIN_VELOCITY
        ) {

          self.momentumFrame =
            window.requestAnimationFrame(
              animate
            );

        } else {

          self.momentumFrame = null;
        }
      }


      this.momentumFrame =
        window.requestAnimationFrame(
          animate
        );
    };


  /* =========================================================
     STOP MOMENTUM
     ========================================================= */

  ReviewCarousel.prototype.stopMomentum =
    function () {

      if (this.momentumFrame) {

        window.cancelAnimationFrame(
          this.momentumFrame
        );

        this.momentumFrame = null;
      }
    };


  /* =========================================================
     CLICK HANDLING
     ========================================================= */

  ReviewCarousel.prototype.handleClick =
    function (event) {

      /*
       * Product links are allowed normally.
       *
       * They cannot start a drag because startDrag()
       * ignores data-rc-product-link elements.
       */
      if (
        event.target.closest(
          '[data-rc-product-link]'
        )
      ) {
        return;
      }


      /*
       * Prevent accidental clicks after dragging.
       */
      if (this.suppressClick) {

        event.preventDefault();
        event.stopPropagation();
      }
    };


  /* =========================================================
     REFRESH
     ========================================================= */

  ReviewCarousel.prototype.refresh =
    function () {

      this.visibleCount =
        this.getVisibleCount();


      this.stopMomentum();


      /*
       * Keep current position before rebuilding
       * navigation measurements.
       */
  var previousCardIndex =
  clamp(
    this.index,
    0,
    this.cards.length - 1
  );


      this.configureNativeScrolling();

      this.buildDots();


      var positions =
        this.getCardPositions();


      if (
        positions[previousCardIndex] !== undefined
      ) {

        var maxScroll =
          this.getMaxScroll();


        this.viewport.scrollLeft =
          clamp(
            positions[previousCardIndex],
            0,
            maxScroll
          );
      }


      this.updateIndexFromScroll();
      this.updateControls();
    };


  /* =========================================================
     RESIZE
     ========================================================= */

  ReviewCarousel.prototype.onResize =
    function () {

      this.refresh();
    };


  /* =========================================================
     EVENTS
     ========================================================= */

  ReviewCarousel.prototype.bindEvents =
    function () {

      var self = this;


      /* -----------------------------------------------------
         NEXT
         ----------------------------------------------------- */

      if (this.nextBtn) {

        this.nextBtn.addEventListener(
          'click',
          function () {
            self.next();
          }
        );
      }


      /* -----------------------------------------------------
         PREVIOUS
         ----------------------------------------------------- */

      if (this.prevBtn) {

        this.prevBtn.addEventListener(
          'click',
          function () {
            self.prev();
          }
        );
      }


      /* -----------------------------------------------------
         Native scrolling
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'scroll',
        this.handleScroll,
        {
          passive: true
        }
      );


      /* -----------------------------------------------------
         Pointer down
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'pointerdown',
        function (event) {

          self.startDrag(event);
        },
        {
          passive: false
        }
      );


      /* -----------------------------------------------------
         Pointer move
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'pointermove',
        function (event) {

          self.drag(event);
        },
        {
          passive: false
        }
      );


      /* -----------------------------------------------------
         Pointer up
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'pointerup',
        function (event) {

          self.endDrag(event);
        },
        {
          passive: true
        }
      );


      /* -----------------------------------------------------
         Pointer cancel
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'pointercancel',
        function (event) {

          self.endDrag(event);
        },
        {
          passive: true
        }
      );


      /* -----------------------------------------------------
         Lost pointer capture
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'lostpointercapture',
        function () {

          if (self.pointerDown) {

            self.pointerDown = false;
            self.isDragging = false;
            self.pointerId = null;
          }
        }
      );


      /* -----------------------------------------------------
         Prevent image dragging while carousel is dragging
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'dragstart',
        function (event) {

          if (self.isDragging) {
            event.preventDefault();
          }
        }
      );


      /* -----------------------------------------------------
         Click suppression
         ----------------------------------------------------- */

      this.viewport.addEventListener(
        'click',
        function (event) {

          self.handleClick(event);
        },
        true
      );


      /* -----------------------------------------------------
         Resize
         ----------------------------------------------------- */

      window.addEventListener(
        'resize',
        this.handleResize
      );
    };


  /* =========================================================
     INITIALIZE
     ========================================================= */

  function init(context) {

    var scope =
      context || document;


    var roots =
      scope.querySelectorAll(
        '[data-review-carousel]'
      );


    for (
      var i = 0;
      i < roots.length;
      i++
    ) {

      var root =
        roots[i];


      if (root.dataset.rcInitialized) {
        continue;
      }


      root.dataset.rcInitialized = 'true';


      new ReviewCarousel(root);
    }
  }


  /* =========================================================
     DOCUMENT READY
     ========================================================= */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init();
      }
    );

  } else {

    init();
  }


  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    function (event) {

      init(event.target);
    }
  );

})();